/**
 * Layout principale del portale: header, nav laterale, area contenuto.
 * Le sezioni non ancora implementate sono elencate in nav come link
 * disabilitati con badge "prossimamente" per comunicare la roadmap.
 */

import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../auth/index.js';
import { UserInfoPopover } from '../components/UserInfoPopover.js';
import { StatusBanner, FreshnessStatus, useDbFreshness } from '../components/StatusBanner.js';
import { PageHelp } from '../components/PageHelp.js';
import { useBranding, useRolePermissions } from '../features/admin/index.js';
import { usePermissions } from '../auth/usePermissions.js';
import { useTicketCounts } from '../features/triage/index.js';
import { TICKETS_PATH } from '../features/triage/routes.js';
import { ManualScanButton } from '../features/prefect/index.js';
import { isTrialEdition } from './edition.js';
import { showsAdminNav } from './adminNav.js';
import styles from './Layout.module.css';

/** Definizione di una voce di navigazione laterale. */
interface NavItem {
  label: string;
  /** Path di destinazione (undefined = non ancora implementato). */
  to?: string;
  /** True se la sezione è implementata e navigabile. */
  available: boolean;
  /** Permesso richiesto per mostrare la voce (assente = visibile a tutti i ruoli autenticati). */
  perm?: string;
  testId?: string;
}

/** Portale completo (edizione `full`, default). */
const FULL_NAV: NavItem[] = [
  { label: 'Dashboard', to: '/', available: true },
  { label: 'ENISA Reports', to: '/enisa-reports', available: true },
  { label: 'Tickets', to: TICKETS_PATH, available: true },
  { label: 'Prodotti', to: '/products', available: true },
  { label: 'Clienti', to: '/customers', available: true },
  { label: 'Batch Edits', to: '/batch-edits', available: true },
  { label: 'Admin', to: '/admin', available: true },
  { label: 'Log', to: '/audit', available: true, perm: 'VIEW_AUDIT_LOG' },
  { label: 'Scheduled Job', to: '/jobs', available: true, testId: 'nav-prefect-link' },
  { label: 'Glossario', to: '/glossary', available: true, testId: 'nav-glossary-link' },
];

/**
 * Edizione `trial` (VITE_EDITION=trial): solo ENISA Reports, Tickets, Prodotti, Clienti, Log,
 * Glossario (+ Admin). Dashboard, Batch Edits e Scheduled Job sono solo NASCOSTE: le route
 * restano raggiungibili via URL.
 */
const TRIAL_NAV: NavItem[] = [
  { label: 'ENISA Reports', to: '/enisa-reports', available: true },
  { label: 'Tickets', to: TICKETS_PATH, available: true },
  { label: 'Prodotti', to: '/products', available: true },
  { label: 'Clienti', to: '/customers', available: true },
  { label: 'Log', to: '/audit', available: true, perm: 'VIEW_AUDIT_LOG' },
  { label: 'Glossario', to: '/glossary', available: true, testId: 'nav-glossary-link' },
  { label: 'Admin', to: '/admin', available: true },
];

export function Layout() {
  const { claims, logout } = useAuth();
  const { data: branding } = useBranding();
  // Seed always-on della mappa RBAC effettiva (queryKey condivisa): popola la
  // cache letta da usePermissions in tutta la UI gated. Endpoint gated
  // VIEW_PORTFOLIO → leggibile da ogni ruolo autenticato.
  useRolePermissions();
  const { can } = usePermissions();
  const { data: triageCounts } = useTicketCounts();
  const logoUrl = branding?.logoUrl ?? null;
  // Stato di freschezza dati vulnerabilità (E10.10 / RF-9-2), condiviso tra il banner
  // (visibile solo se stale) e l'indicatore sempre visibile in header (FreshnessStatus).
  const freshness = useDbFreshness();

  return (
    <div className={styles.shell}>
      {/* Header ---------------------------------------------------------------- */}
      <header className={styles.header} data-testid="layout-header">
        <span className={styles.headerBrandWrap}>
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Logo aziendale"
              className={styles.headerLogo}
              data-testid="header-logo"
            />
          )}
          <span className={styles.headerBrand}>
            <span className={styles.headerBrandAccent}>Portale CRA</span>
          </span>
        </span>
        <FreshnessStatus freshnessOverride={freshness} />
        {claims && (
          <span className={styles.headerActions}>
            <ManualScanButton />
            <span className={styles.headerUser}>
              <UserInfoPopover claims={claims} />
              {' · '}
              <button
                onClick={logout}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#adb5bd',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: 'inherit',
                }}
              >
                Esci
              </button>
            </span>
          </span>
        )}
      </header>

      {/* Banner stato freschezza dati vulnerabilità (E10.10 / RF-9-2) ---------- */}
      {/* Degrada silenziosamente se il monitoring-service (E8) non è disponibile */}
      <StatusBanner freshnessOverride={freshness} />

      <div className={styles.body}>
        {/* Nav laterale -------------------------------------------------------- */}
        <nav className={styles.nav} data-testid="layout-nav">
          <p className={styles.navTitle}>Navigazione</p>
          <ul className={styles.navList}>
            {(isTrialEdition() ? TRIAL_NAV : FULL_NAV)
              .filter((item) => !item.perm || can(item.perm))
              .filter((item) => item.to !== '/admin' || showsAdminNav(claims))
              .map((item) => (
                <li key={item.label} className={styles.navItem}>
                  {item.available && item.to ? (
                    <NavLink
                      to={item.to}
                      end={item.to === '/'}
                      data-testid={item.testId}
                      className={({ isActive }) =>
                        `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
                      }
                    >
                      {item.label}
                      {item.to === TICKETS_PATH && triageCounts && (
                        <span className={styles.navCounters}>
                          {triageCounts.open > 0 && (
                            <span className={styles.navCounterRed} title="Triage da avviare">
                              {triageCounts.open}
                            </span>
                          )}
                          {triageCounts.inTriage > 0 && (
                            <span className={styles.navCounterYellow} title="Triage in corso">
                              {triageCounts.inTriage}
                            </span>
                          )}
                        </span>
                      )}
                    </NavLink>
                  ) : (
                    <span className={`${styles.navLink} ${styles.navLinkDisabled}`}>
                      {item.label}
                      <span className={styles.navBadge}>presto</span>
                    </span>
                  )}
                </li>
              ))}
          </ul>

          <span className={styles.tenaciouscBrand}>
            Tenacious<span className={styles.headerBrandAccent}>C</span>
          </span>
        </nav>

        {/* Contenuto principale ------------------------------------------------ */}
        <main className={styles.main}>
          <Outlet />
          {/* Guida onboarding collassabile in fondo a ogni pagina (contenuto per-route). */}
          <PageHelp />
        </main>
      </div>

      {/* Footer --------------------------------------------------------------- */}
      <footer className={styles.footer}>
        Tenacious C — Piattaforma CRA &nbsp;|&nbsp; Versione{' '}
        {import.meta.env.VITE_COMMIT_SHA?.slice(0, 7) ?? 'dev'}
        {import.meta.env.VITE_DEPLOY_INSTANCE && (
          <>
            &nbsp;|&nbsp; Deploy demo: {import.meta.env.VITE_DEPLOY_INSTANCE}
            {' · azione: '}
            {import.meta.env.VITE_DEPLOY_ACTION}
            {' · seed: '}
            {import.meta.env.VITE_DEPLOY_SEED_FILE || 'none'}
          </>
        )}
      </footer>
    </div>
  );
}
